import { Gift, Send, ArrowUpRight, Lock, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/** Set to false to unlock Send & Transfer after Token Generation Event */
const PRE_TGE = true;

interface RewardsActionsSectionProps {
  claimableRewards: number;
  points: number;
  onShowClaimModal: () => void;
  onShowSendModal: () => void;
  onShowTransferModal: () => void;
}

export function RewardsActionsSection({
  claimableRewards,
  points,
  onShowClaimModal,
  onShowSendModal,
  onShowTransferModal,
}: RewardsActionsSectionProps) {
  const { t } = useTranslation();

  const handleClaimClick = () => {
    onShowClaimModal();
  };

  const handleSendClick = () => {
    if (PRE_TGE) return;
    if (points === 0) {
      toast.error(t('rewards.noRewardsToSend'));
    } else {
      onShowSendModal();
    }
  };

  const handleTransferClick = () => {
    if (PRE_TGE) return;
    if (points === 0) {
      toast.error(t('rewards.noRewardsToTransfer'));
    } else {
      onShowTransferModal();
    }
  };

  return (
    <div className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 min-w-0">
        <div className="min-w-0">
          <h3
            className="text-lg sm:text-xl mb-1 text-[#2D2F7E] dark:text-white break-words"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('rewards.manageYourRewards')}
          </h3>
          <p
            className="text-sm text-[#464452] dark:text-white/70 break-words"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('rewards.claimSendTransferDesc')}
          </p>
        </div>
        {claimableRewards > 0 && (
          <div
            className="px-4 py-2 rounded-full bg-[#FEF3C7] dark:bg-amber-900/20 text-[#D97706] dark:text-amber-300 shrink-0"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {claimableRewards} {t('rewards.petsAvailable')}
          </div>
        )}
      </div>
      {PRE_TGE && (
        <div className="flex items-start gap-2.5 p-3 sm:p-4 mb-4 rounded-xl bg-[#F5F3FF] dark:bg-purple-900/15 border border-[#DDD6FE] dark:border-purple-500/20">
          <Info className="w-4 h-4 mt-0.5 text-[#7C3AED] dark:text-purple-400 shrink-0" />
          <p
            className="text-sm text-[#5B21B6] dark:text-purple-300 leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('rewards.tgeNotice', { feature1: t('rewards.send', 'Send'), feature2: t('rewards.transferToWallet', 'Transfer to Wallet') })}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 min-w-0">
        <button
          onClick={handleClaimClick}
          disabled={claimableRewards === 0}
          className="p-4 sm:p-6 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed border-2 border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16] min-w-0 touch-manipulation"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              }}
            >
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <div
                className="text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.claim')}
              </div>
              <div
                className="text-sm text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.getYourRewards')}
              </div>
            </div>
          </div>
          {claimableRewards > 0 && (
            <div
              className="text-sm text-[#10B981] dark:text-green-300"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              ✓ {claimableRewards} {t('rewards.petsReady')}
            </div>
          )}
        </button>

        <button
          onClick={handleSendClick}
          disabled={PRE_TGE || points === 0}
          className={`relative p-4 sm:p-6 rounded-xl transition-all text-left border-2 min-w-0 touch-manipulation ${
            PRE_TGE
              ? 'opacity-50 cursor-not-allowed border-[#E2DFEA] dark:border-white/10 bg-[#F9F8FC] dark:bg-[#0F0E16]'
              : 'hover:bg-[#F0EFF5] dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16]'
          }`}
        >
          {PRE_TGE && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3F0FF] dark:bg-purple-900/30 border border-[#DDD6FE] dark:border-purple-500/20">
              <Lock className="w-3 h-3 text-[#7C3AED] dark:text-purple-400" />
              <span
                className="text-[10px] font-semibold text-[#7C3AED] dark:text-purple-400"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                TGE
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${PRE_TGE ? 'grayscale' : ''}`}
              style={{
                background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
              }}
            >
              <Send className="w-6 h-6 text-white" />
            </div>
            <div>
              <div
                className="text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.send')}
              </div>
              <div
                className="text-sm text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.toAnotherUser')}
              </div>
            </div>
          </div>
          <div
            className="text-sm text-[#464452] dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {PRE_TGE ? t('rewards.availableAfterTGE') : t('rewards.sendViaUsername')}
          </div>
        </button>

        <button
          onClick={handleTransferClick}
          disabled={PRE_TGE || points === 0}
          className={`relative p-4 sm:p-6 rounded-xl transition-all text-left border-2 min-w-0 touch-manipulation ${
            PRE_TGE
              ? 'opacity-50 cursor-not-allowed border-[#E2DFEA] dark:border-white/10 bg-[#F9F8FC] dark:bg-[#0F0E16]'
              : 'hover:bg-[#F0EFF5] dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#0F0E16]'
          }`}
        >
          {PRE_TGE && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3F0FF] dark:bg-purple-900/30 border border-[#DDD6FE] dark:border-purple-500/20">
              <Lock className="w-3 h-3 text-[#7C3AED] dark:text-purple-400" />
              <span
                className="text-[10px] font-semibold text-[#7C3AED] dark:text-purple-400"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                TGE
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${PRE_TGE ? 'grayscale' : ''}`}
              style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              }}
            >
              <ArrowUpRight className="w-6 h-6 text-white" />
            </div>
            <div>
              <div
                className="text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('rewards.transferToWallet')}
              </div>
              <div
                className="text-sm text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.cashOutSelfCustody')}
              </div>
            </div>
          </div>
          <div
            className="text-sm text-[#464452] dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {PRE_TGE ? t('rewards.availableAfterTGE') : t('rewards.solanaNetwork')}
          </div>
        </button>
      </div>
    </div>
  );
}
